import { useState } from 'react'

import { Eye, EyeOff } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import { getT, toISO8601 } from '@fortem-monorepo/js-utilities'
import { DialogProps } from '@fortem-monorepo/react-ui'
import { SubmitButton } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@fortem-monorepo/react-ui'

import { RunRiskCalibrationForm } from '@/components/forms/RunRiskCalibrationForm/RunRiskCalibrationForm'
import { SchemaType } from '@/components/forms/RunRiskCalibrationForm/RunRiskCalibrationForm.schema'
import { RunRiskCalibrationFormFields } from '@/components/forms/RunRiskCalibrationForm/RunRiskCalibrationFormFields'
import { DEFAULT_RISK } from '@/components/forms/RunRiskCalibrationForm/default-values'
import { useUserContext } from '@/index'
import { FormValues } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/FormValues'

export interface RunRiskCalibrationModalProps extends DialogProps {
  awsActive: boolean
}

export const RunRiskCalibrationModal: React.FC<RunRiskCalibrationModalProps> = ({
  awsActive,
  ...props
}) => {
  const [wantsToSubmit, setWantsToSubmit] = useState(false)
  const [showValues, setShowValues] = useState(false)

  const { isDeveloper } = useUserContext()

  return (
    <Dialog {...props}>
      <DialogContent className={'max-w-225!'}>
        <RunRiskCalibrationForm
          defaultValues={{
            input_type: 'all_jobs',
            instance_tag: 'LOCALINST2',
            value_date: toISO8601(getT()),
          }}
        >
          {({ formState: { isValid, isSubmitting }, subscribe, getValues }) => {
            subscribe({
              name: 'input_type',
              callback: () => {
                setWantsToSubmit(false)
              },
            })

            const input_type = getValues('input_type')

            return (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2">
                    <DialogTitle>Run Jobs</DialogTitle>
                    {isDeveloper() && (
                      <Button
                        type="button"
                        size="sm"
                        variant={'outline'}
                        onClick={() => setShowValues(!showValues)}
                      >
                        {showValues ? <Eye /> : <EyeOff />}
                      </Button>
                    )}
                  </div>
                </DialogHeader>
                <div className="space-y-6 mt-4">
                  {showValues && <FormValues />}
                  <RunRiskCalibrationFormFields />
                </div>
                <DialogFooter className={'justify-start! mt-4'}>
                  {!wantsToSubmit ? (
                    <div className={'flex items-center gap-2 ml-auto'}>
                      <Button
                        type="button"
                        variant={'destructive'}
                        size="sm"
                        onClick={() => props.onOpenChange?.(false)}
                      >
                        Cancel
                      </Button>
                      {input_type === 'all_jobs' ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setWantsToSubmit(true)}
                          disabled={!isValid}
                        >
                          Submit
                        </Button>
                      ) : (
                        <SubmitButton
                          size="sm"
                          disabled={!isValid || isSubmitting}
                          loading={isSubmitting}
                        >
                          Confirm
                        </SubmitButton>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4 mb-4 rounded-md border border-red-600 p-3 text-sm  w-full ">
                      <span className={'text-red-600 font-medium'}>
                        Are you sure you want to do this? This might take a while and could cause
                        performance issues while also overwriting data.
                      </span>
                      <RunAllJobsSummary />
                      <div className={'ml-auto'}>
                        <div className="flex items-center gap-2 w-full">
                          <SubmitButton
                            size="sm"
                            disabled={!isValid || isSubmitting}
                            loading={isSubmitting}
                          >
                            Confirm
                          </SubmitButton>
                          <Button
                            type="button"
                            size="sm"
                            variant={'destructive'}
                            onClick={() => setWantsToSubmit(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </DialogFooter>
              </>
            )
          }}
        </RunRiskCalibrationForm>
      </DialogContent>
    </Dialog>
  )
}

const RunAllJobsSummary = () => {
  const { getValues } = useFormContext<Extract<SchemaType, { input_type: 'all_jobs' }>>()
  const values = getValues()

  return (
    <div className={'flex flex-col gap-2'}>
      <span>The following jobs will run in sequence:</span>
      <div className={'flex flex-col gap-2 border p-2 rounded-md'}>
        <span className={'font-medium'}>Custom Strategies</span>
        <ul className={'space-y-1'}>
          <li>Date: {values.value_date}</li>
          <li>Instance: {values.instance_tag}</li>
        </ul>
      </div>
      <div className={'flex flex-col gap-2 border p-2 rounded-md'}>
        <span className={'font-medium'}>Vol-Div Adjustments</span>
        <ul className={'space-y-1'}>
          <li>Date: {values.value_date}</li>
          <li>Instance: {values.instance_tag}</li>
        </ul>
      </div>
      <div className={'flex flex-col gap-2 border p-2 rounded-md'}>
        <span className={'font-medium'}>Calibration</span>
        <ul className={'space-y-1'}>
          <li>Date: {values.value_date}</li>
          <li>Instance: {values.instance_tag}</li>
          <li>Push to DB: {String(Boolean(values.push_to_db))}</li>
        </ul>
      </div>
      <div className={'flex flex-col gap-2 border p-2 rounded-md'}>
        <span className={'font-medium'}>Risk</span>
        <ul className={'space-y-1'}>
          <li>Date: {values.value_date}</li>
          <li>Instance: {values.instance_tag}</li>
          <li>
            Reports:
            <div className={'border rounded-md w-fit p-2 mt-1'}>
              <table>
                <tbody>
                  {Object.entries(DEFAULT_RISK).map(([key, value], index) => {
                    return (
                      <tr key={index}>
                        <td className={'pr-2'}>{key}</td>
                        <td>{value.join(', ')}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </li>
        </ul>
      </div>
    </div>
  )
}
